/**
 * Cost Breakdown - Detaillierte Kostenaufschlüsselung
 */

import type { CalculatorResult, CalculatorInput } from '@/types/price-calculator';
import { useTranslation } from 'react-i18next';

interface CostBreakdownProps {
  result: CalculatorResult;
  input: CalculatorInput;
}

function CostRow({
  label,
  value,
  indent = false,
  bold = false,
}: {
  label: string;
  value: number;
  indent?: boolean;
  bold?: boolean;
}) {
  if (value === 0 && !bold) return null;

  return (
    <div
      className={`flex justify-between py-1.5 ${indent ? 'pl-4' : ''} ${bold ? 'font-semibold border-t border-neutral-300 dark:border-neutral-600 pt-2 mt-2' : ''}`}
    >
      <span className="text-neutral-700 dark:text-neutral-300">{label}</span>
      <span className="text-neutral-900 dark:text-neutral-100 tabular-nums">
        {value.toFixed(2)} €
      </span>
    </div>
  );
}

export function CostBreakdown({ result, input }: CostBreakdownProps) {
  const { t } = useTranslation();
  return (
    <div className="p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
      <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
        {t('pricing.costBreakdown')}
      </h4>

      <div className="text-sm space-y-0.5">
        <CostRow label={t('pricing.purchasePrice')} value={input.purchasePrice} />

        {/* Plattform */}
        <CostRow label={t('pricing.commission')} value={result.commissionAmount} indent />
        <CostRow label={t('pricing.fixedFee')} value={result.fixedFeeAmount} indent />
        <CostRow label={t('pricing.paymentShort')} value={result.paymentFees} indent />

        {/* FBA */}
        {result.fbaFees > 0 && (
          <>
            <CostRow
              label={t('pricing.fbaFulfillment')}
              value={result.fbaFees - result.fbaGermanySurcharge - result.fbaPeakSurcharge}
              indent
            />
            <CostRow label={t('pricing.fbaGermanySurcharge')} value={result.fbaGermanySurcharge} indent />
            <CostRow label={t('pricing.peakSeason')} value={result.fbaPeakSurcharge} indent />
          </>
        )}

        {/* Sonstiges */}
        <CostRow label={t('pricing.shippingNet')} value={result.shippingCosts} />
        <CostRow label={t('pricing.packaging')} value={result.packagingCosts} />
        <CostRow label={t('pricing.returns')} value={result.returnCosts} />
        <CostRow label={t('pricing.otherCosts')} value={input.otherCosts} />

        {/* Summe */}
        <CostRow label={t('pricing.totalCosts')} value={result.totalCosts} bold />
      </div>

      {/* Effektive Provision */}
      <div className="mt-4 pt-3 border-t border-neutral-200 dark:border-neutral-700">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">{t('pricing.effectivePlatformCosts')}</span>
          <span className="font-medium text-neutral-900 dark:text-neutral-100">
            {result.effectiveCommissionPercent.toFixed(1)}% {t('pricing.ofSalePrice')}
          </span>
        </div>
      </div>
    </div>
  );
}
